import React from 'react';

const Filters = ({ 
  filters, 
  showFilters, 
  onFilterChange, 
  onToggleFilters, 
  onClearFilters, 
  activeFiltersCount, 
  activeFiltersSummary 
}) => {
  return (
    <div className="bg-white rounded-lg shadow mb-6">
      <div className="flex justify-between items-center p-4 border-b border-gray-200">
        <div className="flex items-center space-x-3">
          <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
          {activeFiltersCount > 0 && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              {activeFiltersCount} active
            </span>
          )}
        </div>
        <div className="flex items-center space-x-2">
          {activeFiltersCount > 0 && (
            <button
              onClick={onClearFilters}
              className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded hover:bg-gray-50"
            >
              Clear All
            </button>
          )}
          <button
            onClick={onToggleFilters}
            className="px-3 py-1 text-sm text-blue-600 hover:text-blue-800 border border-blue-300 rounded hover:bg-blue-50 flex items-center"
          >
            {showFilters ? 'Hide Filters' : 'Show Filters'}
            <svg 
              className={`ml-1 h-4 w-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
      </div>
      
      {/* Active Filters Summary (when collapsed) */}
      {!showFilters && activeFiltersCount > 0 && (
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600">Active filters:</span>
            <div className="flex flex-wrap gap-2">
              {activeFiltersSummary.map((filter, index) => (
                <span 
                  key={index}
                  className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                >
                  {filter}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
      
      {showFilters && (
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {/* Form Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Form</label>
              <input
                type="text"
                placeholder="Search forms..."
                value={filters.form}
                onChange={(e) => onFilterChange('form', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Client Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Client</label>
              <input
                type="text"
                placeholder="Search clients..."
                value={filters.client}
                onChange={(e) => onFilterChange('client', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Submitted By Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Submitted By</label>
              <input
                type="text"
                placeholder="Search by name or email..."
                value={filters.submittedBy}
                onChange={(e) => onFilterChange('submittedBy', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={filters.status}
                onChange={(e) => onFilterChange('status', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Statuses</option>
                <option value="draft">Draft</option>
                <option value="pending">Pending</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>

            {/* Pending Time Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Pending Time</label>
              <select
                value={filters.pendingTime}
                onChange={(e) => onFilterChange('pendingTime', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All</option>
                <option value="overdue">Overdue</option>
                <option value="day">Less than 1 day</option>
                <option value="days">1-3 days</option>
                <option value="week">More than 3 days</option>
                <option value="no due date">No due date</option>
              </select>
            </div>

            {/* Products Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Products</label>
              <input
                type="text"
                placeholder="Search products..."
                value={filters.products}
                onChange={(e) => onFilterChange('products', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Documents Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Documents</label>
              <input
                type="text"
                placeholder="e.g., 2/5, 0/3..."
                value={filters.documents}
                onChange={(e) => onFilterChange('documents', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Verifications Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Verifications</label>
              <input
                type="text"
                placeholder="e.g., 1/3, 0/2..."
                value={filters.verifications}
                onChange={(e) => onFilterChange('verifications', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Proforma Invoice Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Proforma Invoice</label>
              <select
                value={filters.proformaInvoice}
                onChange={(e) => onFilterChange('proformaInvoice', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All</option>
                <option value="yes">Has Invoice</option>
                <option value="no">No Invoice</option>
              </select>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Filters; 